import { Text } from '@components'
import type { CreateSetup } from '@here/api'
import { type ArrayXY, SVG } from '@svgdotjs/svg.js'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { Show, createEffect, createSignal } from 'solid-js'
import { Box } from 'styled-system/jsx'
dayjs.extend(duration)

export default function Visualisation(props: {
	setup: CreateSetup | undefined
	class?: string
	height?: string
}) {
	const [error, setError] = createSignal('')
	let svgRef: HTMLDivElement | undefined

	createEffect(() => {
		if (!svgRef || !props.setup) {
			return
		}

		svgRef.innerHTML = ''

		const draw = SVG().addTo(svgRef).size('100%', '100%').viewbox(0, 0, 1000, 1000)

		const padding = 10
		const chartWidth = 1000 - 2 * padding
		const chartHeight = 1000 - 2 * padding

		const priceValues: any[] = [
			props.setup.high_1_price,
			props.setup.high_2_price,
			props.setup.high_3_price,
			props.setup.low_1_price,
			props.setup.low_2_price,
			props.setup.low_3_price,
		]

		const timeValues: any[] = [
			props.setup.high_1_time,
			props.setup.high_2_time,
			props.setup.high_3_time,
			props.setup.low_1_time,
			props.setup.low_2_time,
			props.setup.low_3_time,
		]

		if (priceValues.some((v) => v === null || v === undefined || isNaN(v) || v <= 0)) {
			setError('Unable to generate HVF preview, error found with HVF price points')
			return
		}

		if (timeValues.some((v) => v === null || v === undefined || !dayjs(v).isValid())) {
			setError('Unable to generate HVF preview, error found with HVF time points')
			return
		}

		setError('')

		const data = {
			high1: {
				x: dayjs(props.setup.high_1_time!).valueOf(),
				y: props.setup.high_1_price!,
			},
			high2: {
				x: dayjs(props.setup.high_2_time!).valueOf(),
				y: props.setup.high_2_price!,
			},
			high3: {
				x: dayjs(props.setup.high_3_time!).valueOf(),
				y: props.setup.high_3_price!,
			},
			low1: {
				x: dayjs(props.setup.low_1_time!).valueOf(),
				y: props.setup.low_1_price!,
			},
			low2: {
				x: dayjs(props.setup.low_2_time!).valueOf(),
				y: props.setup.low_2_price!,
			},
			low3: {
				x: dayjs(props.setup.low_3_time!).valueOf(),
				y: props.setup.low_3_price!,
			},
		}

		const scaleX = (x: number) => {
			const minX = Math.min(...Object.values(data).map((d) => d.x))
			const maxX = Math.max(...Object.values(data).map((d) => d.x))
			return ((x - minX) / (maxX - minX)) * chartWidth + padding
		}

		const scaleY = (y: number) => {
			const minY = Math.min(...Object.values(data).map((d) => d.y))
			const maxY = Math.max(...Object.values(data).map((d) => d.y))
			return 1000 - (((y - minY) / (maxY - minY)) * chartHeight + padding)
		}
		// Draw grey zigzag line
		const polylinePoints: ArrayXY[] =
			props.setup.position === 'long'
				? [
						[scaleX(data.high1.x), scaleY(data.high1.y)],
						[scaleX(data.low1.x), scaleY(data.low1.y)],
						[scaleX(data.high2.x), scaleY(data.high2.y)],
						[scaleX(data.low2.x), scaleY(data.low2.y)],
						[scaleX(data.high3.x), scaleY(data.high3.y)],
						[scaleX(data.low3.x), scaleY(data.low3.y)],
					]
				: [
						[scaleX(data.low1.x), scaleY(data.low1.y)],
						[scaleX(data.high1.x), scaleY(data.high1.y)],
						[scaleX(data.low2.x), scaleY(data.low2.y)],
						[scaleX(data.high2.x), scaleY(data.high2.y)],
						[scaleX(data.low3.x), scaleY(data.low3.y)],
						[scaleX(data.high3.x), scaleY(data.high3.y)],
					]
		const greyLine = draw.polyline(polylinePoints).fill('none').stroke({
			color: 'var(--colors-fg-subtle)',
			width: 13,
			linejoin: 'round',
		})

		greyLine.opacity(0).animate(1000).opacity(1)

		const midpointX = (data.low3.x + data.high3.x) / 2
		const midpointY = (data.low3.y + data.high3.y) / 2

		const startX = Math.min(...Object.values(data).map((d) => d.x))
		const endX = Math.max(...Object.values(data).map((d) => d.x))

		draw
			.line(scaleX(startX), scaleY(midpointY), scaleX(endX), scaleY(midpointY))
			// Customize color and opacity as needed
			.stroke({ color: 'var(--colors-fg-subtle)', width: 5, opacity: 0.5 })

		// Draw feint green line for highs
		draw
			.polyline([
				[scaleX(data.high1.x), scaleY(data.high1.y)],
				[scaleX(data.high2.x), scaleY(data.high2.y)],
				[scaleX(data.high3.x), scaleY(data.high3.y)],
			])
			.fill('none')
			.stroke({ color: '#00FF00', width: 8, opacity: 0.3 })

		// Draw feint red line for lows
		draw
			.polyline([
				[scaleX(data.low1.x), scaleY(data.low1.y)],
				[scaleX(data.low2.x), scaleY(data.low2.y)],
				[scaleX(data.low3.x), scaleY(data.low3.y)],
			])
			.fill('none')
			.stroke({ color: '#FF0000', width: 8, opacity: 0.3 })
	})

	return (
		<Show
			when={!error()}
			fallback={
				<Text
					textAlign="center"
					size="xs"
				>
					{error()}
				</Text>
			}
		>
			<Box
				height={props.height || 'auto'}
				class={props.class}
				ref={svgRef!}
			/>
		</Show>
	)
}
